import { useQuery } from '@tanstack/react-query';

import { tipaltiEncryptQueryString } from 'api';
import axios from 'axios';

import extractData from 'utils/api';
import { withReturn } from 'zg_utils/tokens';

const zgClient = axios.create({
  baseURL: '/api/zygoat/',
  responseType: 'json',
  headers: {
    'X-REQUESTED-WITH': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  },
});

export const ZG_QUERY_STALE_TIME = 1000 * 60 * 10; // 10 minutes
export const zgDataQueryKey = ['zygoat-data'];
export const zgDataQueryFn = () => extractData(zgClient.get)('env/');

export const useZygoatEnvVariablesQuery = (overrides = {}) =>
  useQuery({
    queryKey: zgDataQueryKey,
    queryFn: zgDataQueryFn,
    staleTime: ZG_QUERY_STALE_TIME,
    // Allows any failed requests to trigger the error boundary
    useErrorBoundary: true,
    ...overrides,
  });

const useZygoatEnvVariables = () => {
  const { data, isLoading, error } = useZygoatEnvVariablesQuery();

  const ENABLE_STANDARD_FEE_GUARDRAIL = data?.enableStandardFeeGuardrail;
  const ENABLE_FEE_SCHEDULE_REWORK = data?.enableFeeScheduleRework;
  const ENABLE_CLAIM_LICENSING = data?.enableClaimLicensing;
  const ENABLE_LICENSING_ON_REVIEW = data?.enableLicensingOnReview;
  const USE_OFFICE_SERVICE_CONNECTION = data?.useOfficeServiceConnection;
  const USE_FEE_SCHEDULE_FROM_SERVICE_CREATION = data?.useFeeScheduleFromServiceCreation;

  const production = data?.production;

  // Portunus URL constants
  const homeLink = typeof window === 'undefined' ? null : window.location.origin;
  const LOGOUT_URL = data?.portunusUrl ? `${data.portunusUrl}/api/logout` : null;
  const LOGIN_SETTINGS_URL = data?.portunusUrl
    ? withReturn(data.portunusUrl, true, homeLink)
    : null;

  // Tableu URL constants
  const TABLEAU_API = 'https://embedding.tableauusercontent.com/tableau.embedding.3.0.0.min.js';

  // Tipalti URL constants
  const TIPALTI_PAYER = 'MetLifeLegalPlans';

  const PAYEE_DASHBOARD_URL = 'payeedashboard/home';

  const PAYMENT_HISTORY_URL = 'PayeeDashboard/PaymentsHistory';

  const makeURL = (payeeId, baseURL) => {
    // Add the unique payee id and also a timestamp (ts) so that tipalti can verify that the request
    // is current.
    return new Promise(resolve => {
      const baseQuery = `idap=${payeeId}&ts=${Math.round(
        new Date().getTime() / 1000,
      )}&payer=${TIPALTI_PAYER}`;
      tipaltiEncryptQueryString(baseQuery).then(signature => {
        const baseUrl = data?.tipaltiUrl ? `${data.tipaltiUrl}${baseURL}?${baseQuery}` : null;
        resolve(`${baseUrl}&hashkey=${signature}`);
      });
    });
  };

  const makeTipaltiPayeeInfoIframeURL = payeeId => makeURL(payeeId, PAYEE_DASHBOARD_URL);

  const makeTipaltiPaymentsIframeURL = payeeId => makeURL(payeeId, PAYMENT_HISTORY_URL);

  return {
    data,
    isLoading,
    error,
    ENABLE_STANDARD_FEE_GUARDRAIL,
    ENABLE_FEE_SCHEDULE_REWORK,
    ENABLE_CLAIM_LICENSING,
    ENABLE_LICENSING_ON_REVIEW,
    USE_OFFICE_SERVICE_CONNECTION,
    USE_FEE_SCHEDULE_FROM_SERVICE_CREATION,
    production,
    LOGOUT_URL,
    LOGIN_SETTINGS_URL,
    TABLEAU_API,
    makeTipaltiPayeeInfoIframeURL,
    makeTipaltiPaymentsIframeURL,
  };
};

export default useZygoatEnvVariables;
